import { memo } from "react";
import type { GroupCellResult } from "@/lib/availability/groups";
import { cn } from "@/lib/utils";
import type { AvailabilityCellPayload } from "@/schemas/availability";
import type { TipTarget } from "./CellTooltip";
import { IssueCorner } from "./Chips";
import { CELL_CLASS, cellAriaLabel, cellText } from "./cell-style";
import { type ColumnEdge, edgeClass } from "./grid-layout";
import { fullMonthLabel, STATE_LABEL } from "./labels";
import type { GridGroup, ViewRow } from "./model";

export interface TooltipHandlers {
  onTipShow: (target: TipTarget, element: HTMLElement, immediate: boolean) => void;
  onTipHide: () => void;
}

/** Shared wiring so every cell opens the tooltip on hover (after a pause) and on focus. */
function tipProps(handlers: TooltipHandlers, target: () => TipTarget) {
  return {
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) =>
      handlers.onTipShow(target(), event.currentTarget, false),
    onMouseLeave: handlers.onTipHide,
    onFocus: (event: React.FocusEvent<HTMLElement>) =>
      handlers.onTipShow(target(), event.currentTarget, true),
    onBlur: handlers.onTipHide,
  };
}

const SELECTED_CLASS =
  "outline outline-2 -outline-offset-2 outline-solid outline-[color:var(--heading)]";

interface ResourceCellProps extends TooltipHandlers {
  row: ViewRow;
  cell: AvailabilityCellPayload;
  /** Position in the keyboard grid; arrow keys move between these. */
  gridRow: number;
  gridCol: number;
  /** Only one cell in the grid is a Tab stop; arrows move through the rest. */
  tabbable: boolean;
  selected: boolean;
  /** Actual months shown in front of today are drawn fainter. */
  past: boolean;
  edge: ColumnEdge;
  compact: boolean;
  onSelect: (resourceId: string, month: string) => void;
}

/**
 * One person-month. The fill and the signed number carry the state; Proposed hours
 * add a hatch, an issue adds an amber corner, and a selected cell gets a 2px outline.
 */
export const ResourceCell = memo(function ResourceCell({
  row,
  cell,
  gridRow,
  gridCol,
  tabbable,
  selected,
  past,
  edge,
  compact,
  onSelect,
  onTipShow,
  onTipHide,
}: ResourceCellProps) {
  const hasIssue = cell.issues.length > 0;
  return (
    <button
      type="button"
      data-avail-row={gridRow}
      data-avail-col={gridCol}
      tabIndex={tabbable ? 0 : -1}
      aria-label={cellAriaLabel(
        row.name,
        fullMonthLabel(cell.month),
        cell,
        `${cell.hasProposed ? ", includes proposed hours" : ""}${hasIssue ? ", has a data issue" : ""}`,
      )}
      aria-pressed={selected}
      onClick={() => onSelect(row.resourceId, cell.month)}
      {...tipProps({ onTipShow, onTipHide }, () => ({ kind: "resource", row, cell }))}
      className={cn(
        "relative my-[3px] scroll-ml-72 scroll-mt-14 cursor-pointer rounded-sm px-1.5 font-mono text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
        compact ? "h-6" : "h-[34px]",
        CELL_CLASS[cell.state],
        cell.hasProposed && "avail-hatch",
        past && "opacity-[0.62]",
        selected && SELECTED_CLASS,
        edgeClass(edge),
      )}
    >
      {cellText(cell)}
      {hasIssue ? <IssueCorner /> : null}
    </button>
  );
});

interface GroupCellProps extends TooltipHandlers {
  group: GridGroup;
  month: string;
  result: GroupCellResult;
  gridRow: number;
  gridCol: number;
  tabbable: boolean;
  selected: boolean;
  past: boolean;
  edge: ColumnEdge;
  onSelect: (groupKey: string | null, month: string) => void;
}

/**
 * A group's month: Σ Booked ÷ Σ Available over members with a plan, drawn like a
 * person cell. A group that reads fine but holds over-utilized people says so
 * ("2 over") under the number.
 */
export const GroupCell = memo(function GroupCell({
  group,
  month,
  result,
  gridRow,
  gridCol,
  tabbable,
  selected,
  past,
  edge,
  onSelect,
  onTipShow,
  onTipHide,
}: GroupCellProps) {
  const showOver =
    result.overCount > 0 && result.state !== "overUtilized" && result.state !== "stretched";
  return (
    <button
      type="button"
      data-avail-row={gridRow}
      data-avail-col={gridCol}
      tabIndex={tabbable ? 0 : -1}
      aria-label={cellAriaLabel(
        group.label,
        fullMonthLabel(month),
        result,
        result.overCount > 0 ? `, ${result.overCount} over` : "",
      )}
      aria-pressed={selected}
      onClick={() => onSelect(group.key, month)}
      {...tipProps({ onTipShow, onTipHide }, () => ({ kind: "group", group, month, result }))}
      className={cn(
        "relative flex h-[46px] scroll-ml-72 scroll-mt-14 cursor-pointer flex-col items-center justify-center gap-px font-mono text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
        CELL_CLASS[result.state],
        past && "opacity-[0.62]",
        selected && SELECTED_CLASS,
        edgeClass(edge),
      )}
    >
      <span>{cellText(result)}</span>
      {showOver ? (
        <span className="text-xs font-medium text-[color:var(--avail-stretched-fg)]">
          {result.overCount} over
        </span>
      ) : null}
      <span className="sr-only">{STATE_LABEL[result.state]}</span>
    </button>
  );
});
